import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, Shield, Leaf, Package, Phone, Mail, Globe, AlertTriangle } from 'lucide-react';
import { BACKEND_URL } from '../config';

export default function VerifyProductPage() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/product/verify/${productId}`);
        if (!res.ok) throw new Error('Product not found');
        const data = await res.json();
        setProduct(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  if (loading) {
    return (
      <div className="verify-page">
        <div className="verify-loading">
          <div className="verify-spinner"></div>
          <p>Verifying product...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="verify-page">
        <div className="verify-container">
          <div className="verify-error">
            <AlertTriangle size={48} />
            <h2>Product Not Found</h2>
            <p>This product could not be verified. The QR code may be invalid or counterfeit.</p>
            <p className="verify-id">Product ID: {productId}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="verify-page">
      <div className="verify-container">
        {/* Verification Badge */}
        <div className="verify-badge-section" data-testid="verify-badge">
          <div className="verify-check-icon"><CheckCircle size={48} /></div>
          <h1>Genuine Product</h1>
          <p className="verify-subtitle">This product has been verified by BoAI</p>
          <div className="verify-scan-count" data-testid="scan-count">
            <Shield size={16} />
            <span>Scanned {product.scan_count} {product.scan_count === 1 ? 'time' : 'times'}</span>
          </div>
        </div>

        {/* Product Info */}
        <div className="verify-product-card" data-testid="product-info">
          <div className="verify-brand-row">
            <Leaf size={20} />
            <span className="verify-brand">{product.brand}</span>
            <span className="verify-tagline">{product.tagline}</span>
          </div>
          <h2 className="verify-product-name">{product.product_name}</h2>
          <p className="verify-category">{product.category}</p>

          <div className="verify-details-grid">
            <div className="verify-detail"><span className="label">Net Weight</span><span className="value">{product.net_weight}</span></div>
            <div className="verify-detail"><span className="label">MRP</span><span className="value">₹{product.mrp}</span></div>
            <div className="verify-detail"><span className="label">Mfg. Date</span><span className="value">{product.mfg_date}</span></div>
            <div className="verify-detail"><span className="label">Batch No.</span><span className="value">{product.batch_no}</span></div>
            <div className="verify-detail"><span className="label">Best Before</span><span className="value">{product.best_before}</span></div>
            <div className="verify-detail"><span className="label">FSSAI Lic.</span><span className="value">{product.fssai_lic}</span></div>
          </div>
        </div>

        {/* Ingredients */}
        <div className="verify-section">
          <h3><Package size={18} /> Made from 5 Nutritious Grains</h3>
          <div className="verify-ingredients">
            {product.ingredients.map((item) => (
              <div key={item.name} className="ingredient-row">
                <span className="ingredient-name">{item.name}</span>
                <span className="ingredient-pct">{item.percentage}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Nutrition */}
        <div className="verify-section">
          <h3>Nutritional Information (per 100g)</h3>
          <div className="verify-nutrition">
            {Object.entries(product.nutrition).map(([key, val]) => (
              <div key={key} className="nutrition-row">                <span className="nutrition-label">{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                <span className="nutrition-value">{val}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits */}
        <div className="verify-section">
          <h3>Goodness in Every Roti</h3>
          <div className="verify-benefits">
            {product.benefits.map((b) => (
              <div key={b} className="benefit-chip"><CheckCircle size={14} /> {b}</div>
            ))}
          </div>
        </div>

        {/* Marketed By */}
        <div className="verify-section verify-footer-info">
          <h3>Marketed & Packed by</h3>
          <p className="marketed-by">{product.marketed_by}</p>
          <p className="marketed-address">{product.address}</p>
          <div className="verify-contact">
            <a href={`tel:${product.contact.phone}`}><Phone size={14} /> {product.contact.phone}</a>
            <a href={`mailto:${product.contact.email}`}><Mail size={14} /> {product.contact.email}</a>
            <a href={`https://${product.contact.website}`} target="_blank" rel="noreferrer"><Globe size={14} /> {product.contact.website}</a>
          </div>
          <p className="support-farmers">SUPPORT OUR FARMERS • SUPPORT OUR NATION</p>
        </div>
      </div>
    </div>
  );
}
